import { Injectable } from '@angular/core';
import { ToasterService } from '@ui-kit/app-toast/toaster.service';
import { CrudService, GetEntityModel, UpdateEntityModel } from 'collection';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { IAdditionalSettings, Setting } from './settings.model';

@Injectable({
  providedIn: 'root'
})
export class SettingsService {
  additionalSettings$ = new BehaviorSubject<IAdditionalSettings>(null);
  timeTrackingRules$ = new BehaviorSubject<any>(null);
  getAdditionalSettings: GetEntityModel;
  getTimeTrackingRulesRequest: GetEntityModel;
  additionalSettingUpdate: UpdateEntityModel;

  isOpenSidebar: boolean = true;
  private _isOpenSidebar: BehaviorSubject<boolean> = new BehaviorSubject(this.isOpenSidebar);
  get $isOpenSidebar(): Observable<any> {
    return this._isOpenSidebar.asObservable();
  }

  toggleSidebar(): boolean {
    this.isOpenSidebar = !this.isOpenSidebar;
    this._isOpenSidebar.next(this.isOpenSidebar);
    return this.isOpenSidebar;
  }

  errorHandler(err) {
    if (err.data && err.data.length) {
      err.data.forEach((error) => {
        this._toastService.show({ type: 'error', message: error.message });
      });
    } else if (err.data && err.data.message) {
      this._toastService.show({ type: 'error', message: err.data.message });
    } else {
      this._toastService.show({ type: 'error', message: err.message || err });
    }
  }

  setAdditionalSettings(data: IAdditionalSettings) {
    const mappedData = {
      ...data,
      date_inputs_hiding_in_time_entry:
        data.date_inputs_hiding_in_time_entry === 1 || data.date_inputs_hiding_in_time_entry === true,
      enable_lunch_registration: data.enable_lunch_registration === 1 || data.enable_lunch_registration === true,
      enable_registering_expenses: data.enable_registering_expenses === 1 || data.enable_registering_expenses === true,
      travel_time: data.travel_time === 1 || data.travel_time === true,
      trip_length: data.trip_length === 1 || data.trip_length === true,
      mark_hours_as_billable_and_billed:
        data.mark_hours_as_billable_and_billed === 1 || data.mark_hours_as_billable_and_billed === true,
      billable_options_is_checked_by_default:
        data.billable_options_is_checked_by_default === 1 || data.billable_options_is_checked_by_default === true,
      only_administrators_can_mark_as_billable:
        data.only_administrators_can_mark_as_billable === 1 || data.only_administrators_can_mark_as_billable === true,
      only_administrators_can_mark_as_billed:
        data.only_administrators_can_mark_as_billed === 1 || data.only_administrators_can_mark_as_billed === true,
      workers_are_not_able_to_edit_time_entries:
        data.workers_are_not_able_to_edit_time_entries === 1 || data.workers_are_not_able_to_edit_time_entries === true,
      hours_must_be_registered_on_a_project:
        data.hours_must_be_registered_on_a_project === 1 || data.hours_must_be_registered_on_a_project === true,
      tags_must_be_required_on_time_entry:
        data.tags_must_be_required_on_time_entry === 1 || data.tags_must_be_required_on_time_entry === true,
      tasks_required: data.tasks_required === 1 || data.tasks_required === true,
      only_administrators_can_set_salary_code:
        data.only_administrators_can_set_salary_code === 1 || data.only_administrators_can_set_salary_code === true,
      hide_reports_footer_logo: data.hide_reports_footer_logo === 1 || data.hide_reports_footer_logo === true,
      display_background_colour: data.display_background_colour === 1 || data.display_background_colour === true,
      timesheet_approvals: data.timesheet_approvals === 1 || data.timesheet_approvals === true
    };
    this.additionalSettings$.next(mappedData);
  }

  setTimeTrackingRule(data): void {
    this.timeTrackingRules$.next(data);
  }

  get initTimeTrackingRules(): Observable<any> {
    if (!this.timeTrackingRules$.value) {
      return this.getTimeTrackingRulesRequest.get().pipe(
        map((res: { data: any }) => {
          this.setTimeTrackingRule(res.data);
          return res.data;
        })
      );
    } else {
      return this.timeTrackingRules$;
    }
  }

  get initAdditionalSettings(): Observable<any> {
    if (!this.additionalSettings$.value) {
      return this.getAdditionalSettings.get().pipe(
        map((res: { data: any }) => {
          this.setAdditionalSettings(res.data);
          return res.data;
        })
      );
    } else {
      return this.additionalSettings$;
    }
  }

  setUpdateSettingPayload(payload: Setting | Setting[]): { [key: string]: number | boolean } {
    const data = {};
    if (Array.isArray(payload)) {
      payload.forEach((item) => {
        const value = this.mapIfTypeBoolean(item.value);
        data[item.key] = value;
      });
    } else {
      const value = this.mapIfTypeBoolean(payload.value);
      data[payload.key] = value;
    }
    return data;
  }

  private mapIfTypeBoolean(value: number | boolean | string): number | string {
    if (typeof value === 'boolean') {
      return value ? 1 : 0;
    } else {
      return value;
    }
  }

  clearAdditionalSettings() {
    this.additionalSettings$.next(null);
  }

  constructor(
    private _toastService: ToasterService,
    private _crud: CrudService
  ) {
    this.getAdditionalSettings = this._crud.createGetEntity({ name: 'additional-settings' });
    this.additionalSettingUpdate = this._crud.createUpdateEntity({ name: 'additional-settings/edit' });
    this.getTimeTrackingRulesRequest = this._crud.createGetEntity({ name: 'time-tracking/rules' });
  }
}
